import { c as _c } from "react-compiler-runtime";
import { createFocusManager } from "@react-aria/focus";
import { useEvent } from "@react-aria/utils";
import React from "react";
import { mergeProps, useDateField, useDatePicker, useDateSegment, useLocale, useObjectRef } from "react-aria";
import { Button as AriaButton, ButtonContext, Dialog, Group, Input as AriaInput, LabelContext, ListBoxContext, ListStateContext, OverlayTriggerStateContext, Provider, useSlottedContext } from "react-aria-components";
import { useDatePickerState } from "react-stately";
import { useMediaLayout } from "use-media";
import { Calendar as LineCalendar } from "../icons/line.js";
import { Calendar as SolidCalendar } from "../icons/solid.js";
import { mediaQueryMax } from "../vars.js";
import * as styles from "./DatePicker.css.js";
import { ListBox } from "./ListBox.js";
import { Popover } from "./Popover.js";
import { Spinner } from "./Spinner.js";
import { Tab, TabList, TabPanel, Tabs } from "./Tabs.js";
import { FCTextFieldContext } from "./TextFieldContext.js";
import useI18n from "./internal/useI18n.js";
import { useMonthYearDateFieldState } from "./internal/useMonthYearDateFieldState.js";
import { ListBoxItem } from "./shared/DropdownItem.js";
import { InputBuilder } from "./shared/InputBuilder.js";
import { extractInputBuilderProps } from "./shared/inputBuilderHelper.js";
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
const BaseCalendarButton = (t0, ref) => {
  const $ = _c(10);
  const {
    visible: t1,
    excludeFromTabOrder
  } = t0;
  const visible = t1 === undefined ? true : t1;
  const state = React.useContext(OverlayTriggerStateContext);
  const isOpen = state?.isOpen ?? false;
  const isSmallScreen = useMediaLayout(mediaQueryMax.xxs);
  if (isSmallScreen) {
    return null;
  }
  let t2;
  if ($[0] !== isOpen || $[1] !== visible) {
    t2 = styles.calendarButton({
      isOpen,
      visible
    });
    $[0] = isOpen;
    $[1] = visible;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== isOpen) {
    t3 = t4 => {
      const {
        isFocused
      } = t4;
      return isFocused || isOpen ? /*#__PURE__*/_jsx(SolidCalendar, {
        "aria-hidden": "true"
      }) : /*#__PURE__*/_jsx(LineCalendar, {
        "aria-hidden": "true"
      });
    };
    $[3] = isOpen;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== excludeFromTabOrder || $[6] !== ref || $[7] !== t2 || $[8] !== t3) {
    t4 = /*#__PURE__*/_jsx(AriaButton, {
      ref: ref,
      className: t2,
      excludeFromTabOrder: excludeFromTabOrder,
      children: t3
    });
    $[5] = excludeFromTabOrder;
    $[6] = ref;
    $[7] = t2;
    $[8] = t3;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  return t4;
};
const CalendarButton = /*#__PURE__*/React.forwardRef(BaseCalendarButton);
const BaseDateSegment = (t0, ref) => {
  const $ = _c(16);
  const {
    state,
    segment,
    size
  } = t0;
  const domRef = useObjectRef(ref);
  const {
    segmentProps
  } = useDateSegment(segment, state, domRef);
  let t1;
  if ($[0] !== segmentProps) {
    t1 = mergeProps(segmentProps);
    $[0] = segmentProps;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const t2 = segmentProps.style;
  let t3;
  if ($[2] !== segment.type || $[3] !== size) {
    t3 = styles.segment({
      size,
      type: segment.type
    });
    $[2] = segment.type;
    $[3] = size;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const t4 = segment.isPlaceholder || undefined;
  const t5 = state.isInvalid || undefined;
  const t6 = !segment.isEditable || undefined;
  const t7 = state.isDisabled || undefined;
  let t8;
  if ($[5] !== domRef || $[6] !== segment.text || $[7] !== segment.type || $[8] !== segmentProps.style || $[9] !== t1 || $[10] !== t3 || $[11] !== t4 || $[12] !== t5 || $[13] !== t6 || $[14] !== t7) {
    t8 = /*#__PURE__*/_jsx("span", {
      ...t1,
      style: t2,
      className: t3,
      ref: domRef,
      "data-placeholder": t4,
      "data-invalid": t5,
      "data-readonly": t6,
      "data-disabled": t7,
      "data-type": segment.type,
      children: segment.text
    });
    $[5] = domRef;
    $[6] = segment.text;
    $[7] = segment.type;
    $[8] = segmentProps.style;
    $[9] = t1;
    $[10] = t3;
    $[11] = t4;
    $[12] = t5;
    $[13] = t6;
    $[14] = t7;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  return t8;
};
const DateSegment = /*#__PURE__*/React.forwardRef(BaseDateSegment);
const currentYear = new Date().getFullYear();
const startYear = currentYear - 60;
const extraYearsToShow = 1;
const undefinedMonthId = -1;
const useYearsAndMonthsValues = selectedYear => {
  const $ = _c(18);
  const i18n = useI18n();
  const addExtraYear = selectedYear != null && (selectedYear < startYear || selectedYear > currentYear + extraYearsToShow);
  const t0 = selectedYear ?? -1;
  let years;
  if ($[0] !== addExtraYear || $[1] !== selectedYear || $[2] !== t0) {
    const extraYear = {
      id: t0,
      name: selectedYear == null ? "" : selectedYear.toString()
    };
    years = Array.from({
      length: currentYear - startYear + extraYearsToShow
    }).map(_temp).reverse();
    if (addExtraYear && selectedYear > startYear) {
      years = [extraYear, ...years];
    } else {
      if (selectedYear != null && selectedYear < startYear) {
        years = [...years, extraYear];
      }
    }
    $[0] = addExtraYear;
    $[1] = selectedYear;
    $[2] = t0;
    $[3] = years;
  } else {
    years = $[3];
  }
  const {
    locale
  } = useLocale();
  let t1;
  let undefinedValue;
  if ($[4] !== i18n || $[5] !== locale) {
    const formatter = new Intl.DateTimeFormat(locale, {
      month: "long"
    });
    let t2;
    if ($[8] !== i18n) {
      t2 = i18n.format("datePickerDialogMonthUnspecified");
      $[8] = i18n;
      $[9] = t2;
    } else {
      t2 = $[9];
    }
    let t3;
    if ($[10] !== t2) {
      t3 = {
        id: undefinedMonthId,
        name: t2
      };
      $[10] = t2;
      $[11] = t3;
    } else {
      t3 = $[11];
    }
    undefinedValue = t3;
    t1 = Array.from({
      length: 12
    }).map((__0, index) => ({
      id: index + 1,
      name: `${(index + 1).toString().padStart(2, "0")} - ${formatter.format(new Date(1992, index, 1))}`
    }));
    $[4] = i18n;
    $[5] = locale;
    $[6] = t1;
    $[7] = undefinedValue;
  } else {
    t1 = $[6];
    undefinedValue = $[7];
  }
  const months = t1;
  let t2;
  if ($[12] !== months || $[13] !== undefinedValue) {
    t2 = [undefinedValue, ...months];
    $[12] = months;
    $[13] = undefinedValue;
    $[14] = t2;
  } else {
    t2 = $[14];
  }
  let t3;
  if ($[15] !== t2 || $[16] !== years) {
    t3 = {
      years,
      months: t2
    };
    $[15] = t2;
    $[16] = years;
    $[17] = t3;
  } else {
    t3 = $[17];
  }
  return t3;
};
const MonthYearDialog = t0 => {
  const $ = _c(42);
  const {
    size,
    state
  } = t0;
  const i18n = useI18n();
  const {
    years,
    months
  } = useYearsAndMonthsValues(state.year);
  const [selectedTab, setSelectedTab] = React.useState("year");
  const listboxRef = React.useRef(null);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = [[ListBoxContext, {
      selectionMode: "single",
      autoFocus: "first",
      shouldFocusOnHover: true,
      shouldSelectOnPressUp: true,
      escapeKeyBehavior: "none"
    }], [ListStateContext, null]];
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = key => {
      if (key !== "year" && key !== "month") {
        throw new Error(`Unexpected tab selection: ${key}. Expected 'year' or 'month'.`);
      }
      setSelectedTab(key);
    };
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== i18n) {
    t3 = i18n.format("datePickerDialogTabYear");
    $[2] = i18n;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== t3) {
    t4 = /*#__PURE__*/_jsx(Tab, {
      id: "year",
      children: t3
    });
    $[4] = t3;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== i18n) {
    t5 = i18n.format("datePickerDialogTabMonth");
    $[6] = i18n;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] !== t5) {
    t6 = /*#__PURE__*/_jsx(Tab, {
      id: "month",
      children: t5
    });
    $[8] = t5;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[10] !== t4 || $[11] !== t6) {
    t7 = /*#__PURE__*/_jsxs(TabList, {
      children: [t4, t6]
    });
    $[10] = t4;
    $[11] = t6;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  let t8;
  if ($[13] !== i18n) {
    t8 = i18n.format("datePickerDialogYearListLabel");
    $[13] = i18n;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  let t9;
  if ($[15] !== state.year) {
    t9 = state.year == null ? [] : [state.year];
    $[15] = state.year;
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  let t10;
  if ($[17] !== state) {
    t10 = value => {
      if (value === "all") {
        throw new Error("Unexpected selection of 'all' in year selection.");
      }
      const values = [...value].map(_temp2).filter(_temp3);
      if (values.length === 0) {
        state.clearSegment("year");
        return;
      } else {
        if (values.length > 1) {
          throw new Error(`Unexpected number of selected years, got ${values.join(", ")}, expected 1.`);
        }
      }
      state.setSegment("year", values[0]);
      setSelectedTab("month");
    };
    $[17] = state;
    $[18] = t10;
  } else {
    t10 = $[18];
  }
  let t11;
  if ($[19] !== size || $[20] !== t10 || $[21] !== t8 || $[22] !== t9 || $[23] !== years) {
    t11 = /*#__PURE__*/_jsx(TabPanel, {
      ref: listboxRef,
      id: "year",
      children: /*#__PURE__*/_jsx(ListBox, {
        size: size,
        "aria-label": t8,
        items: years,
        selectedKeys: t9,
        onSelectionChange: t10,
        virtualized: false,
        children: _temp4
      })
    });
    $[19] = size;
    $[20] = t10;
    $[21] = t8;
    $[22] = t9;
    $[23] = years;
    $[24] = t11;
  } else {
    t11 = $[24];
  }
  let t12;
  if ($[25] !== i18n) {
    t12 = i18n.format("datePickerDialogMonthListLabel");
    $[25] = i18n;
    $[26] = t12;
  } else {
    t12 = $[26];
  }
  let t13;
  if ($[27] !== state.month) {
    t13 = state.month == null ? [undefinedMonthId] : [state.month];
    $[27] = state.month;
    $[28] = t13;
  } else {
    t13 = $[28];
  }
  let t14;
  if ($[29] !== state) {
    t14 = value_0 => {
      if (value_0 === "all") {
        throw new Error("Unexpected selection of 'all' in month selection.");
      }
      const values_0 = [...value_0].map(_temp5).filter(_temp6);
      if (values_0.length === 0) {
        state.clearSegment("month");
        return;
      } else {
        if (values_0.length > 1) {
          throw new Error(`Unexpected number of selected years, got ${values_0.join(", ")}, expected 1.`);
        }
      }
      if (values_0[0] === undefinedMonthId) {
        state.clearSegment("month");
      } else {
        state.setSegment("month", values_0[0]);
      }
    };
    $[29] = state;
    $[30] = t14;
  } else {
    t14 = $[30];
  }
  let t15;
  if ($[31] !== months || $[32] !== size || $[33] !== t12 || $[34] !== t13 || $[35] !== t14) {
    t15 = /*#__PURE__*/_jsx(TabPanel, {
      id: "month",
      children: /*#__PURE__*/_jsx(ListBox, {
        size: size,
        items: months,
        "aria-label": t12,
        selectedKeys: t13,
        onSelectionChange: t14,
        virtualized: false,
        children: _temp7
      })
    });
    $[31] = months;
    $[32] = size;
    $[33] = t12;
    $[34] = t13;
    $[35] = t14;
    $[36] = t15;
  } else {
    t15 = $[36];
  }
  let t16;
  if ($[37] !== selectedTab || $[38] !== t11 || $[39] !== t15 || $[40] !== t7) {
    t16 = /*#__PURE__*/_jsx(Provider, {
      values: t1,
      children: /*#__PURE__*/_jsxs(Tabs, {
        size: "s",
        type: "filled",
        fullWidth: true,
        selectedKey: selectedTab,
        onSelectionChange: t2,
        children: [t7, t11, t15]
      })
    });
    $[37] = selectedTab;
    $[38] = t11;
    $[39] = t15;
    $[40] = t7;
    $[41] = t16;
  } else {
    t16 = $[41];
  }
  return t16;
};
const useDatePickerLabels = props => {
  const $ = _c(4);
  const fcLabelProps = React.useContext(FCTextFieldContext);
  const labelProps = useSlottedContext(LabelContext, props.slot);
  const t0 = props["aria-label"] ?? fcLabelProps.label;
  const t1 = props["aria-labelledby"] ?? labelProps?.id;
  let t2;
  if ($[0] !== fcLabelProps.labelRef || $[1] !== t0 || $[2] !== t1) {
    t2 = {
      ref: fcLabelProps.labelRef,
      label: t0,
      labelId: t1
    };
    $[0] = fcLabelProps.labelRef;
    $[1] = t0;
    $[2] = t1;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  return t2;
};
const BaseDatePicker = (baseProps, ref) => {
  const {
    inputBuilderProps,
    restProps: props
  } = extractInputBuilderProps(baseProps);
  const labelProps = useDatePickerLabels(props);
  const groupRef = useObjectRef(ref);
  const [userInputtedDateDirectly, setUserInputtedDateDirectly] = React.useState(false);
  const datePickerState = useDatePickerState({
    granularity: "day",
    validationBehavior: "native",
    onOpenChange: isOpen => {
      if (isOpen) {
        setUserInputtedDateDirectly(false);
      }
    }
  });
  const {
    buttonProps,
    dialogProps
  } = useDatePicker({
    validationBehavior: "native",
    shouldForceLeadingZeros: true,
    // eslint-disable-next-line @typescript-eslint/naming-convention
    "aria-labelledby": labelProps.labelId
  }, datePickerState, groupRef);
  const focusManager = React.useMemo(
  // essentially debounce: https://github.com/facebook/react/issues/31290
  // eslint-disable-next-line react-hooks/refs
  () => createFocusManager(groupRef), [groupRef]);

  // This is to ensure we can click on the label and focus the date field.
  // We need to do it like this, as otherwise we can't have a composable <TextField> component. The RAC hook assumes
  // that DateField manages EVERYTHING....which is...messy...
  useEvent(labelProps.ref ?? {
    current: null
  }, "click", e => {
    if (baseProps.skipLabelClickListener === true) {
      return;
    }
    e.stopPropagation();
    focusManager.focusFirst();
  });
  const dateFieldState = useMonthYearDateFieldState(props);
  const inputRef = React.useRef(null);
  const fieldRef = React.useRef(null);
  const {
    fieldProps: dateFieldProps,
    inputProps: dateFieldInputProps
  } = useDateField({
    // eslint-disable-next-line @typescript-eslint/naming-convention
    "aria-label": labelProps.label,
    inputRef,
    name: props.name,
    onFocus: props.onFocus,
    onBlur: e_0 => {
      props.onBlur?.(e_0);
      setUserInputtedDateDirectly(false);
    },
    onKeyDown: e_1 => {
      if (e_1.key === "Tab" || e_1.key === "Enter") {
        return;
      }
      setUserInputtedDateDirectly(true);
    }
  }, dateFieldState, fieldRef);
  return /*#__PURE__*/_jsxs(Provider, {
    values: [[OverlayTriggerStateContext, datePickerState], [ButtonContext,
    // eslint-disable-next-line @typescript-eslint/naming-convention
    {
      ...buttonProps,
      "aria-labelledby": labelProps.labelId
    }]],
    children: [/*#__PURE__*/_jsxs(InputBuilder, {
      slot: props.slot,
      ref: groupRef,
      ...inputBuilderProps,
      right: ({
        loadingState,
        loadingRenderState,
        loadingStartAnimation
      }) => /*#__PURE__*/_jsxs(_Fragment, {
        children: [loadingState == null ? null : /*#__PURE__*/_jsx(Spinner, {
          state: loadingState,
          renderState: loadingRenderState,
          startAnimation: loadingStartAnimation,
          size: "s",
          color: "royalBlue"
        }), /*#__PURE__*/_jsx(CalendarButton, {
          visible: loadingRenderState !== "loading",
          excludeFromTabOrder: props.slot === "from" && userInputtedDateDirectly
        })]
      }),
      children: [/*#__PURE__*/_jsx(Group, {
        ref: fieldRef,
        ...dateFieldProps,
        isInvalid: dateFieldState.isInvalid,
        isDisabled: dateFieldState.isDisabled,
        className: styles.field,
        children: dateFieldState.segments.map((segment, i) => /*#__PURE__*/_jsx(DateSegment, {
          state: dateFieldState,
          segment: segment,
          size: "m"
        }, i))
      }), /*#__PURE__*/_jsx(AriaInput, {
        ...dateFieldInputProps,
        value: dateFieldState.monthYearTextValue,
        ref: inputRef
      })]
    }), /*#__PURE__*/_jsx(Popover, {
      trigger: "DateRange",
      width: 72,
      triggerRef: groupRef,
      placement: "bottom end",
      children: /*#__PURE__*/_jsx(Dialog, {
        ...dialogProps,
        className: styles.dialog,
        children: /*#__PURE__*/_jsx(MonthYearDialog, {
          size: inputBuilderProps.size,
          state: dateFieldState
        })
      })
    })]
  });
};
export const DatePicker = /*#__PURE__*/React.forwardRef(BaseDatePicker);
function _temp(_, i) {
  return {
    id: i + startYear,
    name: (i + startYear).toString()
  };
}
function _temp2(v) {
  return typeof v === "number" ? v : null;
}
function _temp3(v_0) {
  return v_0 !== null;
}
function _temp4(t0) {
  const {
    id,
    name
  } = t0;
  return /*#__PURE__*/_jsx(ListBoxItem, {
    id: id,
    children: name
  });
}
function _temp5(v_1) {
  return typeof v_1 === "number" ? v_1 : null;
}
function _temp6(v_2) {
  return v_2 !== null;
}
function _temp7(t0) {
  const {
    id: id_0,
    name: name_0
  } = t0;
  return /*#__PURE__*/_jsx(ListBoxItem, {
    id: id_0,
    children: name_0
  });
}
//# sourceMappingURL=DatePicker.js.map